// entities/adornisEntity.ts
import { A } from "@adornis/base/env-info";
import { logger } from "@adornis/base/logging";
import MapBehaviorSubject from "@adornis/base/rxjs-extension/reactives/map";
import "@adornis/validation/validator";
import { DateTime } from "luxon";
import { BehaviorSubject, combineLatest, of } from "rxjs";
import { distinctUntilChanged, filter, map, switchMap } from "rxjs/operators";
import { Float, ID, Int, isDateTime, isGQLScalarType } from "../baseqlTypes";
import { EntityStore } from "../client/entityStore";
import { metaDataStore } from "../metaDataStore";
import { selectionSet } from "../utils/queryGeneration";
var upgradeMark = Symbol("upgraded");
var emptyState = Symbol("empty");
var fromNetwork = Symbol("fromNetwork");
var _manualUpdate = "___manualUpdate";
var NotImplementedError = class extends Error {
};
var _a;
var AdornisEntity = class {
  constructor(doc) {
    this[_a] = true;
    this._hasMetaObservable = false;
    this._metaPaused = false;
    this.isFromNetwork = doc[fromNetwork];
    const fields = this.constructor.fields;
    this.doc = {};
    this.docMeta = {
      [_manualUpdate]: {
        omitted: false,
        state: new BehaviorSubject({ value: null, dirty: false })
      }
    };
    fields.forEach((field) => {
      if (doc[field.name] && typeof doc[field.name] === "object")
        doc[field.name][fromNetwork] = this.isFromNetwork;
      const value = constructValue(
        field.options.default && !this.isFromNetwork ? field.options.default(doc[field.name]) : doc[field.name],
        field
      );
      const state = new BehaviorSubject({ value: emptyState, dirty: false });
      this.doc[field.name] = value;
      this.docMeta[field.name] = { state, omitted: value === void 0 };
    });
    this.doc._class = this.constructor._class;
    const propertyDescriptors = Object.fromEntries(
      fields.map((field) => [
        field.name,
        {
          get: () => {
            const value = this.getValue(field.name);
            if (this.isFromNetwork && this.getMetaData(field.name).omitted && value === void 0) {
              logger.error({ entity: this.doc._class, field: field.name }, "entity missing data");
              throw new Error(
                `Trying to access property ${field.name} on ${this.doc._class}, but it was not requested from remote`
              );
            }
            return value;
          },
          set: (value) => {
            const metaData = this.getMetaData(field.name);
            if (value && typeof value === "object" && !value[upgradeMark])
              value = constructValue(value, field);
            const dirty = !(field.options.compare || defaultCompare)(this.doc[field.name], value, field);
            metaData.state.next({ value, dirty });
            if (this._hasMetaObservable) {
              this.redoFieldReactive(field);
            }
            return true;
          }
        }
      ])
    );
    Object.defineProperties(this, propertyDescriptors);
    this.isNew = !this.isFromNetwork;
  }
  static get metaTreeNode() {
    const myNode = this._metaTreeNode[this._class];
    if (myNode)
      return myNode;
    const node = metaDataStore.find((node2) => node2.type() === this);
    if (!node)
      throw new Error(
        `could not find entity with name ${this._class} in metaDataTree. Is the entity correctly decorated?`
      );
    return this._metaTreeNode[this._class] = node;
  }
  static get fields() {
    const myFields = this._fields[this._class];
    if (myFields)
      return myFields;
    return this._fields[this._class] = metaDataStore.getAllFields(this.metaTreeNode, -1);
  }
  static resolveID(entity) {
    throw new NotImplementedError(`${this._class} does not implement resolveID`);
  }
  static get allFields() {
    return selectionSet(() => this, 2);
  }
  static getByID(id) {
    throw new Error(`${this._class} does not implement getByID`);
  }
  makeFieldReactive(field, visited = /* @__PURE__ */ new Set()) {
    visited.add(this);
    const value = this.getValue(field.name);
    const metaData = this.docMeta[field.name];
    if (!metaData)
      throw new Error(`no metaData for field ${field.name} found. Something is very wrong.`);
    if (!metaData.metaSub && value?.[upgradeMark] && !visited.has(value)) {
      if (!metaData.metaObs)
        metaData.metaObs = value?.getMetaObservable?.(visited);
      metaData.metaSub = metaData.metaObs?.subscribe((valueMeta) => {
        const state = metaData.state.getValue();
        const newDirty = state.value !== emptyState || valueMeta.some((m) => m.dirty);
        metaData.state.next({ value: state.value, dirty: newDirty });
      });
    }
  }
  redoFieldReactive(field) {
    const metaData = this.docMeta[field.name];
    if (!metaData)
      throw new Error(`no metaData for field ${field.name} found. Something is very wrong.`);
    metaData.metaSub?.unsubscribe();
    delete metaData.metaSub;
    delete metaData.metaObs;
    this.makeFieldReactive(field);
  }
  getMetaObservable(visited = /* @__PURE__ */ new Set()) {
    this._hasMetaObservable = true;
    visited.add(this);
    this.constructor.fields.forEach((field) => {
      this.makeFieldReactive(field, visited);
    });
    return combineLatest(Object.values(this.docMeta).map((x) => x.state)).pipe(filter((_) => !this._metaPaused));
  }
  get whenChanging() {
    return this.getMetaObservable().pipe(map(() => this));
  }
  get dirty() {
    return this.getMetaObservable().pipe(
      map((meta) => meta.some((m) => m.dirty)),
      distinctUntilChanged()
    );
  }
  getMetaData(fieldName) {
    const metaData = this.docMeta[fieldName];
    if (!metaData)
      throw new Error(`no metaData for field ${fieldName} found. Something is very wrong.`);
    return metaData;
  }
  getValue(fieldName) {
    const state = this.getMetaData(fieldName).state.getValue();
    if (state.dirty && state.value !== emptyState)
      return state.value;
    return this.doc[fieldName];
  }
  has(fieldName) {
    return !this.isFromNetwork || !this.docMeta[fieldName]?.omitted;
  }
  get isComplete() {
    return !this.constructor.fields.reduce(
      (acc, field) => acc || this.getMetaData(field.name).omitted,
      false
    );
  }
  async validate(validateOmitted = true, validated = /* @__PURE__ */ new Set()) {
    if (!this.validator)
      return;
    validated.add(this);
    for (let [key, validationFunction] of Object.entries(this.validator.functions)) {
      const metaData = this.getMetaData(key);
      const state = metaData.state.getValue();
      if (state.value === emptyState && !validateOmitted && metaData.omitted)
        continue;
      const toCheck = this.getValue(key);
      try {
        await validationFunction(toCheck, key, this);
      } catch (e) {
        console.error("Error while validating entity:", this);
        throw e;
      }
      if (toCheck?.[upgradeMark] && !validated.has(toCheck))
        await toCheck.validate(validateOmitted, validated);
    }
  }
  async commit(visited = /* @__PURE__ */ new Set()) {
    await this._commit(visited);
    this.docMeta[_manualUpdate]?.state.next({ value: null, dirty: false });
  }
  async _commit(visited = /* @__PURE__ */ new Set()) {
    visited.add(this);
    this._metaPaused = true;
    await this.validate(!this.isFromNetwork);
    const fields = this.constructor.fields;
    for (let i = 0; i < fields.length; i++) {
      const field = fields[i];
      const metaData = this.getMetaData(field.name);
      const state = metaData.state.getValue();
      if (state.value !== emptyState) {
        if (state.value?.[upgradeMark] && !visited.has(state.value))
          await state.value._commit(visited);
        this.doc[field.name] = state.value;
        metaData.state.next({ value: emptyState, dirty: false });
        metaData.omitted = this.doc[field.name] === void 0;
      } else if (this.doc[field.name]?.[upgradeMark] && !visited.has(this.doc[field.name])) {
        await this.doc[field.name]._commit(visited);
      }
    }
    this._metaPaused = false;
  }
  revert(visited = /* @__PURE__ */ new Set()) {
    this._revert(visited);
    this.docMeta[_manualUpdate]?.state.next({ value: null, dirty: false });
  }
  _revert(visited = /* @__PURE__ */ new Set()) {
    visited.add(this);
    this._metaPaused = true;
    this.constructor.fields.forEach((field) => {
      const value = this.doc[field.name];
      const metaData = this.getMetaData(field.name);
      metaData.state.next({ value: emptyState, dirty: false });
      if (this._hasMetaObservable)
        this.redoFieldReactive(field);
      if (value?.[upgradeMark] && !visited.has(value))
        value._revert(visited);
    });
    this._metaPaused = false;
  }
  isDirty(recurse = true, visited = /* @__PURE__ */ new Set()) {
    visited.add(this);
    return this.constructor.fields.some((field) => {
      const value = this.doc[field.name];
      const metaData = this.getMetaData(field.name);
      if (metaData.state.getValue().dirty)
        return true;
      if (value?.[upgradeMark] && !visited.has(value) && recurse)
        return value.isDirty(recurse, visited);
    });
  }
  remove() {
    if (A.isClient)
      EntityStore.drop(this);
  }
  mergeData(data, isFromNetwork) {
    if (data === void 0)
      return;
    if (data === null || !Object.keys(data).length) {
      console.warn("mergeData called without data!", this, data);
      return;
    }
    this.constructor.fields.forEach((field) => {
      if (this.doc[field.name]?.[upgradeMark]) {
        if (field.options.merge) {
          const merged = field.options.merge(this.doc[field.name], data[field.name], field, this);
          const state = this.getMetaData(field.name).state.getValue();
          if (merged !== this.doc[field.name] && state.value === emptyState) {
            this.doc[field.name] = merged;
            if (this._hasMetaObservable)
              this.redoFieldReactive(field);
          }
        } else {
          this.doc[field.name].mergeData(data[field.name], isFromNetwork);
        }
      } else {
        if (data[field.name] && typeof data[field.name] === "object")
          data[field.name][fromNetwork] = isFromNetwork;
        const newValue = constructValue(data[field.name], field);
        if (!(field.options.compare || defaultCompare)(this.doc[field.name], newValue, field)) {
          const metaData = this.getMetaData(field.name);
          this.doc[field.name] = (field.options.merge || defaultMerge)(
            this.doc[field.name],
            data[field.name],
            field,
            this,
            isFromNetwork
          );
          if (this.doc[field.name] !== void 0)
            metaData.omitted = false;
          metaData.state.next(metaData.state.getValue());
        }
      }
    });
  }
  async _save(...args) {
    this.isNew = false;
    await this.commit();
    if (A.isClient)
      EntityStore.getAndMerge(this.toObject()) || EntityStore.put(this);
  }
  async save(...args) {
    return this._save();
  }
  toJSON() {
    return this.toObject(true);
  }
  toObject(filterResolved = false, visited = /* @__PURE__ */ new Set()) {
    visited.add(this);
    const me = Object.fromEntries(
      this.constructor.fields.map((field) => {
        if (filterResolved && field.options.resolve && !field.options.includeInParameter)
          return [field.name, void 0];
        const state = this.getMetaData(field.name).state.getValue();
        const intermediate = state.dirty && state.value !== emptyState ? state.value : this.doc[field.name];
        const value = intermediate && intermediate[upgradeMark] ? visited.has(intermediate) ? void 0 : intermediate.toObject(filterResolved, visited) : toJSPrimitive(intermediate, field.type);
        return [field.name, value];
      }).filter((pair) => pair[1] !== void 0)
    );
    visited.delete(this);
    return me;
  }
  static validSubclasses(node = this.metaTreeNode) {
    return metaDataStore.reduce(
      (acc, node2) => {
        acc.push(node2.type()._class);
        return acc;
      },
      [],
      node
    );
  }
};
_a = upgradeMark;
AdornisEntity._class = "AdornisEntity";
AdornisEntity._metaTreeNode = {};
AdornisEntity._fields = {};
var defaultCompare = (oldValue, newValue, field) => {
  if (field.type() === Date)
    return (oldValue?.getTime() ?? oldValue) === (newValue?.getTime() ?? newValue);
  return oldValue === newValue;
};
var defaultMerge = (localValue, remoteValue, field, entity, isFromNetwork) => {
  if (remoteValue !== void 0) {
    if (remoteValue && typeof remoteValue === "object")
      remoteValue[fromNetwork] = isFromNetwork;
    return constructValue(remoteValue, field);
  }
  return localValue;
};
var constructValue = (value, field) => {
  if (value && value[upgradeMark])
    return value;
  if (value === null || value === void 0)
    return value;
  if (field && field.arrayDepth())
    return constructArrayValue(value, field, field.arrayDepth());
  const upgrade = A.isClient ? EntityStore.getAndMerge(value, field) || EntityStore.put(applyConstructor(value, field?.type), field) : applyConstructor(value, field?.type);
  return upgrade;
};
var applyConstructor = (value, typeFunc) => {
  const type = typeFunc?.();
  if (isDateTime(type)) {
    if (DateTime.isDateTime(value))
      return value;
    if (typeof value === "string")
      return DateTime.fromISO(value);
    if (typeof value === "number")
      return DateTime.fromMillis(value);
    return DateTime.fromJSDate(value);
  }
  switch (type) {
    case String:
    case ID:
      return String(value);
    case Boolean:
      return Boolean(value);
    case Int:
    case Float:
      return Number(value);
    case Date:
      return new Date(value);
    default:
      if (value._class !== void 0 && value._class !== type?._class) {
        const node = metaDataStore.find((node2) => node2.type()._class === value._class);
        if (!node)
          throw new Error(
            `Unable to instantiate unknown entity ${value._class}. No constructor found in metaDataStore.`
          );
        return new (node.type())(value);
      }
      if (!type)
        throw new Error(
          `called applyConstructor without type definition and value does not contain a _class field! ${JSON.stringify(
            value
          )}`
        );
      return new type(value);
  }
};
var toJSPrimitive = (value, typeFunc) => {
  const type = typeFunc();
  if (isDateTime(type)) {
    return DateTime.isDateTime(value) ? value.toJSDate() : value;
  }
  return value;
};
var constructArrayValue = (value, field, arrayDepth) => {
  if (value && value[upgradeMark])
    return value;
  const isScalar = isGQLScalarType(field.type);
  if (!arrayDepth) {
    if (value === void 0 || value === null)
      return value;
    const intermediate = A.isClient ? EntityStore.getAndMerge(value, field) || EntityStore.put(applyConstructor(value, field.type), field) : applyConstructor(value, field.type);
    return intermediate;
  }
  const isFromNetwork = value[fromNetwork];
  const array = [];
  const workArray = [];
  const arrayState = new BehaviorSubject({ value: null, dirty: false });
  const itemMetaData = new MapBehaviorSubject();
  let metaPaused = false;
  const getMetaObservable = (visited = /* @__PURE__ */ new Set()) => {
    if (!itemMetaData.getValue().size && !isScalar)
      workArray.forEach((item) => itemMetaData.set(item, {}));
    return itemMetaData.pipe(
      filter((_) => !metaPaused),
      switchMap((metaData) => {
        const observables = isScalar ? [] : Array.from(metaData.entries()).map(([value2, metaData2]) => {
          if (value2 === _manualUpdate)
            return of({ value: null, dirty: false });
          if (!metaData2.metaObs)
            metaData2.metaObs = value2.getMetaObservable(visited);
          return metaData2.metaObs.pipe(map((valueMeta) => ({ value: null, dirty: valueMeta.some((m) => m.dirty) })));
        });
        observables.unshift(arrayState);
        return combineLatest(observables);
      })
    );
  };
  const updateMetaData = () => {
    if (isScalar)
      return;
    metaPaused = true;
    Array.from(itemMetaData.keys()).forEach((item) => {
      if (!workArray.includes(item) && item !== _manualUpdate) {
        itemMetaData.get(item)?.metaSub?.unsubscribe();
        itemMetaData.delete(item);
      }
    });
    workArray.forEach((item) => {
      if (!itemMetaData.has(item)) {
        itemMetaData.set(item, {});
      }
    });
    metaPaused = false;
    itemMetaData.set(_manualUpdate, {});
  };
  if (!value.forEach)
    console.log("DEBUG constructArrayValue on non array value!", value, field, arrayDepth);
  value.forEach((entry, index) => {
    if (!isScalar && entry)
      entry[fromNetwork] = isFromNetwork;
    const constructed = constructArrayValue(entry, field, arrayDepth - 1);
    array[index] = constructed;
    workArray[index] = constructed;
  });
  const proxiedArray = new Proxy(workArray, {
    get: (target, name) => {
      if (name === upgradeMark)
        return true;
      if (name === "getMetaObservable") {
        return getMetaObservable;
      }
      if (name === "dirty") {
        return getMetaObservable().pipe(
          map((meta) => meta.some((m) => m.dirty)),
          distinctUntilChanged()
        );
      }
      if (name === "whenChanging") {
        return getMetaObservable().pipe(map(() => proxiedArray));
      }
      if (name === "_commit")
        return async function(visited = /* @__PURE__ */ new Set()) {
          array.length = workArray.length;
          for (let i = 0; i < workArray.length; i++) {
            const entry = workArray[i];
            if (entry?.[upgradeMark] && !visited.has(entry))
              await entry._commit(visited);
            array[i] = entry;
          }
          updateMetaData();
          arrayState.next({ value: null, dirty: false });
        };
      if (name === "_revert")
        return function(visited) {
          metaPaused = true;
          workArray.forEach((entry) => {
            if (entry?.[upgradeMark] && !visited.has(entry))
              entry._revert(visited);
          });
          workArray.length = array.length;
          array.forEach((entry, index) => {
            workArray[index] = entry;
          });
          metaPaused = false;
          updateMetaData();
          arrayState.next({ value: null, dirty: false });
        };
      if (name === "isDirty")
        return function(recurse = true, visited = /* @__PURE__ */ new Set()) {
          const state = arrayState.getValue();
          if (state.dirty)
            return true;
          if (!isScalar && recurse)
            return workArray.some(
              (entry) => entry?.[upgradeMark] && !visited.has(entry) && entry.isDirty(recurse, visited)
            );
          return false;
        };
      if (name === "mergeData")
        return function(data, mergeIsFromNetwork) {
          if (!data)
            return;
          if (!Array.isArray(data))
            throw new Error(`Cannot merge array with non-array data! data: ${data}, array: ${array}`);
          array.length = data.length;
          const state = arrayState.getValue();
          if (!state.dirty)
            workArray.length = data.length;
          data.forEach((entry, index) => {
            if (!isScalar)
              entry[fromNetwork] = mergeIsFromNetwork;
            const constructed = constructArrayValue(entry, field, arrayDepth - 1);
            array[index] = constructed;
            if (!state.dirty)
              workArray[index] = constructed;
          });
          updateMetaData();
          arrayState.next({ value: null, dirty: !compareArrays(array, workArray, field) });
        };
      if (name === "toObject")
        return function(filterResolved = false, visited = /* @__PURE__ */ new Set()) {
          return workArray.map(
            (entry) => entry?.[upgradeMark] ? visited.has(entry) ? void 0 : entry.toObject(filterResolved, visited) : toJSPrimitive(entry, field.type)
          ).filter((entry) => entry !== void 0);
        };
      if (name === "validate")
        return async function(validateOmitted = true, validated = /* @__PURE__ */ new Set()) {
          for (let i = 0; i < workArray.length; i++) {
            const entry = workArray[i];
            entry?.[upgradeMark] && !validated.has(entry) && await entry.validate(validateOmitted, validated);
          }
        };
      if (typeof target[name] === "function") {
        return function() {
          const result = workArray[name].apply(workArray, arguments);
          if (["copyWithin", "fill", "pop", "push", "reverse", "shift", "sort", "splice", "unshift"].includes(
            name
          )) {
            const changed = !compareArrays(array, workArray, field);
            updateMetaData();
            arrayState.next({ value: null, dirty: changed });
          }
          return result;
        };
      }
      return target[name];
    },
    set: (target, name, value2) => {
      if (accessingArrayEntries(name)) {
        workArray[name] = value2;
        updateMetaData();
        arrayState.next({ value: null, dirty: !compareArrays(array, workArray, field) });
        return true;
      }
      if (name === "length") {
        workArray.length = value2;
        updateMetaData();
        arrayState.next({ value: null, dirty: !compareArrays(array, workArray, field) });
        return true;
      }
      target[name] = value2;
      return true;
    }
  });
  return proxiedArray;
};
var compareArrays = (array, workArray, field) => {
  if (array.length !== workArray.length)
    return false;
  return array.every((_, index) => defaultCompare(array[index], workArray[index], field));
};
var accessingArrayEntries = (index) => typeof index !== "symbol" && !isNaN(+index) && index !== "";
export {
  AdornisEntity,
  NotImplementedError,
  constructValue,
  fromNetwork,
  upgradeMark
};
